import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  NgZone,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** Shared shell state; feature permissions and destinations remain in NavigationComponent. */
@Injectable({ providedIn: 'root' })
export class NavigationStateService {
  private readonly document = inject(DOCUMENT);
  private readonly window = this.document.defaultView;
  private readonly media = this.window.matchMedia('(max-width: 767px)');
  readonly mobile = signal(this.media.matches);
  readonly mobileOpen = signal(false);
  readonly desktopExpanded = signal(this.readPreference());
  readonly expanded = computed(() =>
    this.mobile() ? this.mobileOpen() : this.desktopExpanded()
  );

  constructor() {
    const destroyRef = inject(DestroyRef);
    const zone = inject(NgZone);
    const resize = (event: MediaQueryListEvent) =>
      zone.run(() => {
        this.mobile.set(event.matches);
        this.closeMobile();
      });
    this.media.addEventListener('change', resize);
    destroyRef.onDestroy(() => {
      this.media.removeEventListener('change', resize);
      this.document.body.classList.remove(
        'opened',
        'kuba-navigation-overlay-open'
      );
    });
    effect(() => {
      this.document.body.classList.toggle('opened', this.expanded());
      this.document.body.classList.toggle(
        'kuba-navigation-overlay-open',
        this.mobileOpen()
      );
      // Focus after the overlay has become visible, rather than before its CSS state updates.
      if (this.mobileOpen()) {
        this.window.requestAnimationFrame(() => {
          if (this.mobileOpen()) this.focusElement('kuba-navigation-close');
        });
      }
    });
    inject(Router)
      .events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => this.closeMobile());
  }

  toggle(): void {
    if (this.mobile()) {
      this.mobileOpen.update(open => !open);
    } else {
      this.desktopExpanded.update(expanded => !expanded);
      try {
        this.window.localStorage.setItem(
          'kuba.navigation.expanded',
          String(this.desktopExpanded())
        );
      } catch {
        // Navigation remains usable when browser storage is unavailable.
      }
    }
  }

  closeMobile(): void {
    if (!this.mobileOpen()) return;
    this.mobileOpen.set(false);
    this.window.requestAnimationFrame(() => {
      this.focusElement('kuba-navigation-toggle');
    });
  }

  private focusElement(id: string): void {
    const element = this.document.getElementById(id);
    if (element instanceof HTMLElement) element.focus();
  }

  private readPreference(): boolean {
    try {
      return (
        this.window.localStorage.getItem('kuba.navigation.expanded') !== 'false'
      );
    } catch {
      return true;
    }
  }
}
